import { useEffect, useRef } from "react";

export default function HeroSection() {
  const lineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = lineRef.current;
    if (!el) return;
    let frame: number;
    let dir = 1;
    let pos = 0;
    const animate = () => {
      pos += 0.4 * dir;
      if (pos > 12) dir = -1;
      if (pos < 0) dir = 1;
      el.style.transform = `translateY(${pos}px)`;
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ background: "linear-gradient(160deg, #ffffff 0%, #f4f6fb 60%, #eef1f8 100%)" }}
    >
      {/* Background decorative blobs */}
      <div className="absolute top-20 right-10 w-72 h-72 rounded-full opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(circle, #a8c8ff 0%, transparent 70%)" }} />
      <div className="absolute bottom-32 left-10 w-56 h-56 rounded-full opacity-15 pointer-events-none"
        style={{ background: "radial-gradient(circle, #7de8c8 0%, transparent 70%)" }} />

      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10 text-center w-full">
        {/* Eyebrow label */}
        <div className="inline-flex items-center gap-2 mb-8">
          <span className="w-8 h-px bg-gray-400" />
          <span className="text-xs font-semibold tracking-widest text-gray-500 uppercase">iOS Development Studio</span>
          <span className="w-8 h-px bg-gray-400" />
        </div>

        {/* Main headline */}
        <h1 className="text-5xl md:text-7xl font-bold text-gray-900 leading-tight mb-6"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Modern iOS
          <br />
          <span className="text-transparent bg-clip-text"
            style={{ backgroundImage: "linear-gradient(90deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)" }}>
            Applications
          </span>
        </h1>

        <p className="text-xl md:text-2xl text-gray-500 font-light max-w-2xl mx-auto leading-relaxed mb-16">
          Built for performance &amp; security — enhancing user experience on every device.
        </p>

        {/* Device mockups row */}
        <div className="relative flex items-end justify-center gap-4 md:gap-8 h-64 md:h-80 mb-16">
          {/* Left device */}
          <div className="relative w-28 md:w-36 h-52 md:h-64 rounded-3xl overflow-hidden shadow-xl"
            style={{ transform: "rotate(-8deg) translateY(20px)", background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)" }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <i className="ri-shield-check-line text-white text-sm" />
              </div>
              <div className="w-full h-1.5 rounded-full bg-white/20" />
              <div className="w-3/4 h-1.5 rounded-full bg-white/10" />
              <div className="w-full h-1.5 rounded-full bg-white/10" />
            </div>
            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-10 h-1.5 rounded-full bg-white/30" />
          </div>

          {/* Center device (largest) */}
          <div className="relative w-36 md:w-48 h-64 md:h-80 rounded-3xl overflow-hidden shadow-2xl z-10"
            style={{ transform: "rotate(2deg)", background: "linear-gradient(135deg, #0f3460 0%, #533483 100%)" }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-5">
              <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
                <i className="ri-smartphone-line text-white text-xl" />
              </div>
              <div className="w-full h-2 rounded-full bg-white/25" />
              <div className="w-4/5 h-2 rounded-full bg-white/15" />
              <div className="w-full h-2 rounded-full bg-white/15" />
              <div className="w-3/5 h-2 rounded-full bg-white/10" />
            </div>
            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-12 h-1.5 rounded-full bg-white/30" />
          </div>

          {/* Right device */}
          <div className="relative w-28 md:w-36 h-52 md:h-64 rounded-3xl overflow-hidden shadow-xl"
            style={{ transform: "rotate(6deg) translateY(30px)", background: "linear-gradient(135deg, #00b894 0%, #00cec9 100%)" }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <i className="ri-sparkling-line text-white text-sm" />
              </div>
              <div className="w-full h-1.5 rounded-full bg-white/20" />
              <div className="w-3/4 h-1.5 rounded-full bg-white/10" />
              <div className="w-full h-1.5 rounded-full bg-white/10" />
            </div>
            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-10 h-1.5 rounded-full bg-white/30" />
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="flex flex-col items-center gap-2">
          <span className="text-xs text-gray-400 tracking-widest uppercase">Scroll</span>
          <div ref={lineRef} className="w-px h-10 bg-gradient-to-b from-gray-400 to-transparent" />
        </div>
      </div>
    </section>
  );
}
